<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { Grid } from '@progress/kendo-vue-grid';
import type { IAoeCart } from '../../types/CartTypes'
import type { IOrderTypePoePriceCatItem } from '../../types/CommonTypes'
import { GetOrderTypePoePriceCatFromLocalStorageOrApi } from '../../services/CommonService'
import type { IAoeCartItem } from '../../types/CartTypes'
import { FormatCurrency } from '../../services/currencyFormatter'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

const logger = useLogger()

const props = defineProps<{
  cart: IAoeCart | null
  orderTypeId: string
}>()
watch(() => props.cart, async (newCart) => {
  logger.debug('cart changed in AoePriceCategoryTotals', newCart)
  await getGridData()
}, { deep: true })
watch(() => props.orderTypeId, async (newOrderTypeId) => {
  logger.debug('OrderTypeId changed in AoePriceCategoryTotals', newOrderTypeId)
  await getGridData()
}, { deep: true })

// Column definitions for the Kendo grid
const columns = [
  {
    field: 'name',
    title: t('partyOrderEntry.PriceCategoryName'),
    width: resolveColumnWidth('200px', t('partyOrderEntry.PriceCategoryName')),
  },
  {
    field: 'totalPrice',
    title: t('partyOrderEntry.SumTotalPrice'),
    format: '{0:c}',
    cell: 'totalPriceCell',
    width: resolveColumnWidth('150px', t('partyOrderEntry.SumTotalPrice')),
  },
  {
    field: 'discountPercentage',
    title: t('partyOrderEntry.Discount'),
    cell: 'discountPercentageCell',
    width: resolveColumnWidth('120px', t('partyOrderEntry.Discount')),
  },
  {
    field: 'finalPrice',
    title: t('partyOrderEntry.FinalSum'),
    format: '{0:c}',
    cell: 'finalPriceCell',
    width: resolveColumnWidth('150px', t('partyOrderEntry.FinalSum')),
  }
]

const categoryPricingData = ref<IOrderTypePoePriceCatItem[] | null>(null)
const cartBuyingDiscount = ref<number>(0)

onMounted(async () => {
  logger.debug('AoePriceCategoryTotals onMounted')
  await getGridData()
})

// Calculate the total price for items in a specific category
function calculateCategoryTotal(categoryPriceItem: IOrderTypePoePriceCatItem): number {
  if (!props.cart?.items?.length) return 0

  return props.cart.items
    .filter((x: IAoeCartItem) => {
      return categoryPriceItem.categoryCodes.includes(x.categoryCode)
    })
    .reduce((total: number, item: IAoeCartItem) => {
      return total + (item.retailPrice || 0) * (item.quantity || 0)
    }, 0)
}

// Calculate the final price after discount
function calculateFinalPrice(categoryPriceItem: IOrderTypePoePriceCatItem): number {
  const totalPrice = calculateCategoryTotal(categoryPriceItem)
  const discountPercentage = categoryPriceItem.buyingDiscountPercentOverride
    ? categoryPriceItem.buyingDiscountPercentOverride
    : (categoryPriceItem.applyDiscount ? (props.cart?.buyingDiscountPercentageCustomer || 0) : 0)

  return totalPrice * (1 - discountPercentage)
}

async function getGridData() {
  logger.debug('Loading price category data')
  if (!props.cart) return

  cartBuyingDiscount.value = props.cart.buyingDiscountPercentageCustomer ? props.cart.buyingDiscountPercentageCustomer * 100 : 0

  GetOrderTypePoePriceCatFromLocalStorageOrApi({ orderTypeId: props.orderTypeId }).then(data => {
    if (data) {
      categoryPricingData.value = data
    }
  })
}

</script>

<template>
  <div v-if="props.cart != null" class="aoe-price-category-totals">
    <div class="row mt-3 party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div">
        {{ t('partyOrderEntry.ProductSubtotalByCategory') }}
      </div>
    </div>

    <div v-if="categoryPricingData && categoryPricingData.length > 0" class="row mt-3">
      <div class="col-12">
        <Grid
          :data-items="categoryPricingData"
          :columns="columns"
          :resizable="true"
          :pageable="false"
          class="price-category-grid">

          <template v-slot:totalPriceCell="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="total-price-cell">
              {{ FormatCurrency(calculateCategoryTotal(props.dataItem), props.cart?.currencyId) }}
            </td>
          </template>

          <template v-slot:discountPercentageCell="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="discount-percentage-cell">
              {{ props.dataItem.buyingDiscountPercentOverride ? props.dataItem.buyingDiscountPercentOverride * 100 : (props.dataItem.applyDiscount === true ? cartBuyingDiscount : 0) }}%
            </td>
          </template>

          <template v-slot:finalPriceCell="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="final-price-cell">
              {{ FormatCurrency(calculateFinalPrice(props.dataItem),props.cart?.currencyId) }}
            </td>
          </template>

        </Grid>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.aoe-price-category-totals {
  margin-bottom: 20px;
  padding-right: 10px; /* Add padding to create space between components */
}

.party-order-entry-section-header-div {
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-4;
  background-color: transparent;
  border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark));
  margin-bottom: 0;
  box-sizing: border-box; /* Ensure padding is included in width calculation */
}

.price-category-grid {
  width: 100%;
  box-sizing: border-box;
}

/* Fix grid alignment and hide scrollbars */
.price-category-grid {
  overflow: hidden; /* Hide overflow for entire grid container */
}

/* For both header and content areas */
.price-category-grid .k-grid-header,
.price-category-grid .k-grid-content {
  margin-right: 0 !important; /* Remove margin that Kendo adds for scrollbar */
  padding-right: 0 !important; /* Remove padding that might be added for scrollbar space */
  overflow-x: hidden; /* Hide horizontal scrollbar */
}

/* For specific scrollbar targeting */
.price-category-grid .k-grid-content::-webkit-scrollbar,
.price-category-grid .k-grid-header::-webkit-scrollbar {
  display: none; /* Hide scrollbar in WebKit browsers */
  width: 0; /* For extra certainty */
}

/* For IE, Edge, and Firefox */
.price-category-grid .k-grid-content,
.price-category-grid .k-grid-header {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Ensure all columns in header and body align perfectly */
.price-category-grid .k-grid-header-wrap,
.price-category-grid .k-grid-content-wrap {
  border-right: 0; /* Remove border that might affect alignment */
}

/* Ensure full width for better column alignment */
.price-category-grid .k-grid-header-wrap table,
.price-category-grid .k-grid-content table {
  width: 100% !important;
}

.discount-percentage-cell {
  text-align: right;
}

.total-price-cell,
.final-price-cell {
  text-align: right;
}

.k-grid td {
  vertical-align: middle;
}

/* Fix grid container to respect boundaries */
.k-grid-container {
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  overflow: hidden;
}

/* Ensure header stays within bounds */
.k-grid-header {
  border-bottom: 1px solid #e0e0e0;
}
</style>
